import React from 'react';
import styled, { keyframes } from 'styled-components';

// Shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Base skeleton element with theme-aware gradient
const SkeletonBase = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? `linear-gradient(
          90deg,
          rgba(255, 255, 255, 0.03) 0%,
          rgba(255, 255, 255, 0.08) 50%,
          rgba(255, 255, 255, 0.03) 100%
        )`
      : `linear-gradient(
          90deg,
          rgba(0, 0, 0, 0.04) 0%,
          rgba(0, 0, 0, 0.08) 50%,
          rgba(0, 0, 0, 0.04) 100%
        )`};
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${(props) => props.radius || '4px'};
`;

const TableContainer = styled.div`
  width: 100%;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.08)'};
  border-radius: 8px;
  overflow: hidden;
`;

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 120px 100px 100px 120px 80px;
  gap: 16px;
  padding: 16px 20px;
  align-items: center;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(0,0,0,0.05)'};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const SkeletonCheckbox = styled(SkeletonBase)`
  width: 20px;
  height: 20px;
  border-radius: 4px;
`;

const SkeletonAvatar = styled(SkeletonBase)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
`;

const SkeletonText = styled(SkeletonBase)`
  height: ${(props) => props.height || '16px'};
  width: ${(props) => props.width || '100%'};
`;

const SkeletonBadge = styled(SkeletonBase)`
  height: 24px;
  width: 80px;
  border-radius: 12px;
`;

const CustomerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CustomerDetails = styled.div`
  flex: 1;
`;

const TableSkeleton = ({ rows = 8 }) => {
  return (
    <TableContainer>
      {/* Header skeleton */}
      <SkeletonRow style={{ background: 'rgba(250, 70, 22, 0.02)' }}>
        <SkeletonCheckbox />
        <SkeletonText height="14px" width="80px" />
        <SkeletonText height="14px" width="60px" />
        <SkeletonText height="14px" width="50px" />
        <SkeletonText height="14px" width="70px" />
        <SkeletonText height="14px" width="90px" />
        <SkeletonText height="14px" width="50px" />
      </SkeletonRow>

      {/* Body rows skeleton */}
      {Array.from({ length: rows }, (_, index) => (
        <SkeletonRow key={index}>
          <SkeletonCheckbox />
          <CustomerInfo>
            <SkeletonAvatar />
            <CustomerDetails>
              <SkeletonText
                height="16px"
                width="120px"
                style={{ marginBottom: '4px' }}
              />
              <SkeletonText height="14px" width="160px" />
            </CustomerDetails>
          </CustomerInfo>
          <SkeletonBadge />
          <SkeletonText height="18px" width="60px" />
          <SkeletonText height="16px" width="40px" />
          <SkeletonText height="14px" width="80px" />
          <SkeletonText height="32px" width="60px" radius="6px" />
        </SkeletonRow>
      ))}
    </TableContainer>
  );
};

export default TableSkeleton;
